/**
 * Admin conversation debug view.
 * Opens from the overview Details button and reuses the thread transcript.
 */
import { useCallback, useMemo, useRef } from 'react';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Alert, App, Button, Col, Empty, Flex, Row, Skeleton, Typography, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import PageTransition from '@/components/ui/PageTransition';
import {
  ThreadMessages,
  useChronologicalThreadMessages,
  type ChatMessage
} from '@/components/threads/thread';
import { useClientsWithCompaniesQuery } from '@/api/auth/queries';
import {
  useAdminConversationMessagesQuery,
  useAdminConversationThreadQuery,
  useThreadFlowExecutionsQuery,
  useVoiceCallDetailQuery
} from '@/api/chat/queries';
import {
  ConversationDetailHeader,
  ConversationDetailSidebar,
  ExchangeFrame,
  buildBackstageUrl,
  buildExchangeIndex
} from '@/components/conversation-overview';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { jsonPreview } from '@/utils/jsonPreview';

const { Text } = Typography;

const MESSAGE_LIMIT = 1000;

export const Route = createFileRoute('/admin/conversation-overview/$threadId')({
  validateSearch: z.object({ completionId: z.string().optional() }),
  component: ConversationDetailPage
});

function ConversationDetailPage() {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { threadId } = Route.useParams();
  const { completionId } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { data: clientsData } = useClientsWithCompaniesQuery({ page: 1, limit: 500 });
  const threadQuery = useAdminConversationThreadQuery(threadId, completionId);
  const messagesQuery = useAdminConversationMessagesQuery(threadId);
  const executionsQuery = useThreadFlowExecutionsQuery(threadId);

  const thread = threadQuery.data?.thread;
  const completion = threadQuery.data?.completion ?? null;
  const callId = completion?.callId ?? null;
  const voiceQuery = useVoiceCallDetailQuery(callId, !!callId);

  const webchatMessages = messagesQuery.data?.messages ?? [];
  const { chronologicalMessages, groupedMessages } = useChronologicalThreadMessages(
    'webchat',
    webchatMessages,
    []
  );

  const executions = executionsQuery.data?.data ?? [];
  const flowId = thread?.flowId || executions[0]?.clientFlowId || null;
  const clientId = completion?.clientId || thread?.clientId || null;
  const tenantName = useMemo(() => {
    if (!clientId) return null;
    return clientsData?.clients.find((client) => client.id === clientId)?.name ?? clientId;
  }, [clientId, clientsData]);

  const sessionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const msg of chronologicalMessages) {
      if (msg.senderSessionId) ids.add(msg.senderSessionId);
    }
    for (const execution of executions) {
      if (execution.sessionId) ids.add(execution.sessionId);
    }
    return [...ids];
  }, [chronologicalMessages, executions]);

  const exchangeIndex = useMemo(
    () => buildExchangeIndex(chronologicalMessages, executions),
    [chronologicalMessages, executions]
  );

  const { copy } = useCopyToClipboard();
  const copyText = useCallback(
    (value: string) => copy(value, { successMessage: t('conversationOverview.detail.copied') }),
    [copy, t]
  );

  const renderMessage = useCallback(
    (messageItem: ChatMessage, node: React.ReactNode) => {
      const meta = exchangeIndex.get(messageItem.id);
      if (!meta) return node;
      return (
        <ExchangeFrame
          isFirst={meta.firstMessageId === messageItem.id}
          isLast={meta.lastMessageId === messageItem.id}
          turn={meta.turn}
          correlationId={meta.correlationId}
          execution={meta.execution}
          onCopy={() => void copyText(meta.correlationId)}
        >
          {node}
        </ExchangeFrame>
      );
    },
    [copyText, exchangeIndex]
  );

  const goBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({ to: '/admin/conversation-overview' });
  };

  if (threadQuery.isLoading) {
    return (
      <PageTransition animation="fadeInUp">
        <Skeleton active paragraph={{ rows: 8 }} />
      </PageTransition>
    );
  }

  if (threadQuery.isError || !thread) {
    return (
      <PageTransition animation="fadeInUp">
        <Flex justify="center" align="center" style={{ minHeight: 400 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical align="center">
                <Text>{t('conversationOverview.detail.notFound')}</Text>
                <Text type="secondary">{t('conversationOverview.detail.notFoundDescription')}</Text>
              </Flex>
            }
          >
            <Button type="primary" icon={<ArrowLeftOutlined />} onClick={goBack}>
              {t('conversationOverview.detail.back')}
            </Button>
          </Empty>
        </Flex>
      </PageTransition>
    );
  }

  const channel = completion?.channel ?? thread.channel ?? 'WEBCHAT';
  const flag = completion?.flag;
  const flowUrl =
    flowId && clientId
      ? buildBackstageUrl(
          `/automatization/client/flow/${flowId}?clientId=${encodeURIComponent(clientId)}`
        )
      : flowId
        ? buildBackstageUrl(`/automatization/client/flow/${flowId}`)
        : null;
  const messagesTruncated = webchatMessages.length >= MESSAGE_LIMIT;
  const executionsTruncated =
    (executionsQuery.data?.total ?? 0) > (executionsQuery.data?.data.length ?? 0);
  const contextSummary = jsonPreview(thread.contextSummary);
  const voiceContext = jsonPreview(thread.voiceCompletionContext);
  const voiceCall = voiceQuery.data?.call;

  return (
    <PageTransition animation="fadeInUp">
      <ConversationDetailHeader
        tenantName={tenantName}
        channel={channel}
        threadStatus={thread.status}
        flag={flag}
        customerHelped={completion?.customerHelped}
        threadId={thread.id}
        callId={callId}
        flowUrl={flowUrl}
        onBack={goBack}
        onCopy={(value) => void copyText(value)}
      />

      <Row gutter={[token.marginMD, token.marginMD]}>
        <Col xs={24} lg={8}>
          <ConversationDetailSidebar
            flag={flag}
            completion={completion}
            threadId={thread.id}
            tenantName={tenantName}
            flowId={flowId}
            callId={callId}
            sessionIds={sessionIds}
            voiceCall={voiceCall}
            voiceLoading={voiceQuery.isLoading}
            contextSummary={contextSummary}
            voiceContext={voiceContext}
          />
        </Col>
        <Col xs={24} lg={16}>
          <Flex vertical gap={token.marginSM}>
            {messagesTruncated ? (
              <Alert
                type="info"
                showIcon
                title={t('conversationOverview.detail.truncatedMessages')}
              />
            ) : null}
            {executionsTruncated ? (
              <Alert
                type="info"
                showIcon
                title={t('conversationOverview.detail.truncatedExecutions')}
              />
            ) : null}
            <style>{`.conversation-exchange > * { margin-bottom: 0 !important; }`}</style>
            {channel === 'EMAIL' ? (
              <Alert type="info" showIcon title={t('conversationOverview.detail.emailSummaryOnly')} />
            ) : (
              <ThreadMessages
                messagesContainerRef={messagesContainerRef}
                groupedMessages={groupedMessages}
                messageCount={chronologicalMessages.length}
                isActive={thread.status === 'ACTIVE'}
                isLoading={messagesQuery.isLoading}
                isFetching={messagesQuery.isFetching}
                newestMessage={chronologicalMessages.at(-1) ?? null}
                hasMessages={chronologicalMessages.length > 0}
                renderMessage={renderMessage}
                scrollMaxHeight="calc(100vh - 280px)"
              />
            )}
          </Flex>
        </Col>
      </Row>
    </PageTransition>
  );
}
